import { useEffect, useState } from 'react'
import { EventCalendar } from '@/types/Calendar'
import { Daycare } from '@/types/Daycare'
import { SelectOptionsDeprecated } from '@/types/Select'
import { StaffWithLinks } from '@/types/Staff'
import { getSelectOptionsFilters } from './filters/setupFilterOptions.ts'
import { getFilteredEvents, getSortedStaffs } from './filters/filters.ts'
import { Parent } from '@/types/Parent'
import { OccasionalSlotsPlaces } from '@/types/Slots'
import { DateRateSlot } from '@/types/StaffRate'
import { PlanningRights, PlanningTabsState } from '@/hooks/usePlanningRights'
import { getClosingDaycareDays } from '../utils/calendarParameters.ts'

interface usePlanningFiltersParams {
    t: (key: string) => string
    planningTabs: PlanningTabsState
    planningRights: PlanningRights
    calendarEvents: EventCalendar[]
    staffsListData: StaffWithLinks[]
    currentDaycare: Daycare
    places: OccasionalSlotsPlaces[]
    rateSlots: DateRateSlot[]
    parent?: Parent
}

interface usePlanningFiltersState {
    selectFilters: string[]
    setSelectFilters: (filters: string[]) => void
    selectFiltersOptions: SelectOptionsDeprecated
    sortedStaffs: StaffWithLinks[]
    hiddenDays: number[]
    filteredCalendarEvents: EventCalendar[]
    filterEvents: (filters: string[]) => void
}

export function usePlanningFilters({
    t,
    planningTabs,
    planningRights,
    calendarEvents,
    staffsListData,
    currentDaycare,
    places,
    rateSlots,
    parent
}: usePlanningFiltersParams): usePlanningFiltersState {
    const [selectFilters, setSelectFilters] = useState<string[]>([])
    const [selectFiltersOptions, setSelectFiltersOptions] = useState<SelectOptionsDeprecated>([])

    const [sortedStaffs, setSortedStaffs] = useState<StaffWithLinks[]>(staffsListData)
    const [filteredCalendarEvents, setFilteredCalendarEvents] = useState<EventCalendar[]>([])

    const [hiddenDays, setHiddenDays] = useState<number[]>([])

    function filterEvents(filters: string[]) {
        const hasLevel: boolean = filters.some((f: string): boolean => f.startsWith('LEVEL_'))

        setSortedStaffs(hasLevel ? getSortedStaffs(staffsListData, filters, currentDaycare.id) : staffsListData)

        setSelectFilters(filters)

        setFilteredCalendarEvents(getFilteredEvents(planningTabs.activeTabId, planningRights, calendarEvents, filters))
    }

    useEffect(() => {
        setSelectFiltersOptions(
            getSelectOptionsFilters(
                t,
                planningTabs.activeTabId,
                currentDaycare,
                places,
                rateSlots,
                planningRights.canReadStaffWorks,
                parent?.childs
            )
        )

        setFilteredCalendarEvents(
            getFilteredEvents(planningTabs.activeTabId, planningRights, calendarEvents, selectFilters)
        )

        setSortedStaffs(staffsListData)
    }, [planningTabs, planningRights, calendarEvents, places, rateSlots, parent, staffsListData])

    useEffect(() => {
        const hiddenDays: number[] = getClosingDaycareDays(currentDaycare.opening)
        setHiddenDays(hiddenDays.length === 7 ? [] : hiddenDays)

        setSelectFiltersOptions(
            getSelectOptionsFilters(
                t,
                planningTabs.activeTabId,
                currentDaycare,
                places,
                rateSlots,
                planningRights.canReadStaffWorks,
                parent?.childs
            )
        )
    }, [currentDaycare])

    return {
        selectFilters,
        setSelectFilters,
        selectFiltersOptions,
        sortedStaffs,
        hiddenDays,
        filteredCalendarEvents,
        filterEvents
    }
}
